import React, { createContext, useState, useEffect, useContext, useMemo } from 'react';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
  updateProfile,
  updatePassword,
  GoogleAuthProvider,
  signInWithPopup,
  deleteUser as firebaseDeleteUser
} from 'firebase/auth';
import { auth, db } from '../firebase';
import { doc, getDoc, onSnapshot, setDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [premiumData, setPremiumData] = useState({});

  function signup(email, password) {
    return createUserWithEmailAndPassword(auth, email, password);
  }

  function login(email, password) {
    return signInWithEmailAndPassword(auth, email, password);
  }

  function logout() {
    return signOut(auth);
  }

  function resetPassword(email) {
    return sendPasswordResetEmail(auth, email);
  }

  function updateUserProfile(profile) {
    return updateProfile(auth.currentUser, profile);
  }

  function updateUserPassword(newPassword) {
    return updatePassword(auth.currentUser, newPassword);
  }

  function deleteUser() {
    return firebaseDeleteUser(auth.currentUser);
  }

  function signInWithGoogle() {
    const provider = new GoogleAuthProvider();
    return signInWithPopup(auth, provider);
  }

  // Custom function to mark email as verified using Firebase Function
  async function markEmailAsVerified(userId) {
    try {
      console.log('Attempting to mark email as verified for userId:', userId);
      console.log('Current auth user:', auth.currentUser);
      console.log('Auth UID:', auth.currentUser?.uid);
      
      // Call Firebase function instead of writing to Firestore directly
      const functions = getFunctions();
      const markVerified = httpsCallable(functions, 'markEmailAsVerified');
      
      await markVerified({ userId });
      
      console.log('Successfully marked email as verified via Firebase function');
      
      // Update the customEmailVerified property in the state
      setPremiumData(prevData => ({
        ...prevData,
        customEmailVerified: true
      }));
      
      console.log('Updated customEmailVerified to true in state');
    } catch (error) {
      console.error('Error marking email as verified:', error);
      console.error('Error code:', error.code);
      console.error('Error message:', error.message);
      throw error;
    }
  }

  // Custom function to check if email is verified
  async function isEmailVerified(userId) {
    try {
      console.log('Checking if email is verified for userId:', userId);
      
      // Check Firestore for verification status
      const docRef = doc(db, 'verifiedEmails', userId);
      const docSnap = await getDoc(docRef);
      const isVerified = docSnap.exists() && docSnap.data()?.verified === true;
      
      console.log('Email verification status (Firestore):', isVerified);
      
      // Update the customEmailVerified property in the state
      setPremiumData(prevData => ({
        ...prevData,
        customEmailVerified: isVerified
      }));
      
      return isVerified;
    } catch (error) {
      // A permission-denied read looks identical to "not verified" unless we
      // say so explicitly, which cost real debugging time.
      console.error(
        `Error checking email verification (code=${error?.code || 'unknown'}):`,
        error
      );
      return false;
    }
  }

  // Create a stable user object using useMemo
  const stableUser = useMemo(() => {
    if (!currentUser) return null;
    
    const userWithPremium = {
      ...currentUser,
      ...premiumData
    };
    
    console.log('🔄 stableUser updated:', {
      uid: userWithPremium.uid,
      email: userWithPremium.email,
      customEmailVerified: userWithPremium.customEmailVerified,
      isPremium: userWithPremium.isPremium,
      premiumData: premiumData
    });
    
    return userWithPremium;
  }, [currentUser?.uid, currentUser?.email, premiumData.customEmailVerified, premiumData.isPremium, premiumData.isTrial, premiumData.trialEndsAt]);

  // Debug logging for premiumData changes
  useEffect(() => {
    console.log('📊 premiumData changed:', premiumData);
  }, [premiumData]);

  useEffect(() => {
    let mounted = true;
    let premiumUnsubscribe = null;
    let verifiedEmailsUnsubscribe = null;

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!mounted) return;

      if (user) {
        // Set the basic user object immediately
        setCurrentUser(user);

        // Check custom email verification status
        let customVerified = await isEmailVerified(user.uid);

        // Google (and any provider that vouches for the address) has already
        // verified this email, so don't strand those users on /verify-email
        // waiting for a code that was never sent.
        if (!customVerified) {
          const providerVerified = user.emailVerified ||
            (user.providerData || []).some(p => p.providerId === 'google.com');
          if (providerVerified) {
            try {
              await markEmailAsVerified(user.uid);
              customVerified = true;
            } catch (err) {
              console.error('Could not auto-verify provider email:', err);
            }
          }
        }
        
        // Only now is it safe to render routes. Clearing loading before this
        // resolved meant RequireVerifiedEmail saw customEmailVerified as
        // undefined for a tick and bounced verified users to /verify-email,
        // which then sent them a fresh code.
        setLoading(false);

        // Set up real-time listener for premium status
        premiumUnsubscribe = onSnapshot(doc(db, 'premiumUsers', user.uid), (doc) => {
          if (!mounted) return;
          const data = doc.data() || {};
          const isPremium = doc.exists() && data.subscriptionActive === true;
          const isTrial = doc.exists() && data.isTrial === true;
          const trialEndsAt = doc.exists() ? data.trialEndsAt : null;
          console.log('Premium status updated:', { isPremium, isTrial });
          
          // Update premium data separately to avoid unnecessary re-renders
          setPremiumData({
            isPremium,
            isTrial,
            trialEndsAt,
            customEmailVerified: customVerified
          });
        }, (error) => {
          console.error('Error listening to premium status:', error);
        });
        
        // Set up real-time listener for email verification status
        verifiedEmailsUnsubscribe = onSnapshot(doc(db, 'verifiedEmails', user.uid), (doc) => {
          if (!mounted) return;
          const isVerified = doc.exists() && doc.data()?.verified === true;
          console.log('Email verification status updated (real-time):', isVerified);
          
          // Update the customEmailVerified property in premiumData
          setPremiumData(prevData => ({
            ...prevData,
            customEmailVerified: isVerified
          }));
        }, (error) => {
          console.error('Error listening to email verification status:', error);
        });
      } else {
        setCurrentUser(null);
        setPremiumData({});
        setLoading(false); // Set loading to false if no user
      }
    });

    return () => {
      mounted = false;
      unsubscribe();
      if (premiumUnsubscribe) {
        premiumUnsubscribe();
      }
      if (verifiedEmailsUnsubscribe) {
        verifiedEmailsUnsubscribe();
      }
    };
  }, []);

  const value = {
    currentUser: stableUser,
    loading,
    signup,
    login,
    logout,
    resetPassword,
    updateUserProfile,
    updateUserPassword,
    deleteUser,
    signInWithGoogle,
    markEmailAsVerified,
    isEmailVerified
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
} 